import { Card, Tag, StatusBadge } from "@/components/ui-kit";
import { Link, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Inbox, MapPin, Calendar, Wallet, Clock, Globe, Users, BadgeCheck, Crown, Check, Bell } from "lucide-react";
import { fileUrl, api, formatError } from "@/api";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import { AffiliateCard } from "@/components/AffiliateCard";
import { isAffiliate } from "@/lib/affiliate";

export function ProBadge({ className = "", testid = "pro-badge" }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full bg-[#D5E5F9] text-[#292827] px-2 py-0.5 text-[11px] font-medium ${className}`}>
      <BadgeCheck size={12} /> Pro
    </span>
  );
}

export function ProComingSoon({ features }) {
  const { user, setUser } = useAuth();
  const [interested, setInterested] = useState(!!user?.pro_interest);
  const [loading, setLoading] = useState(false);

  const notifyMe = async () => {
    if (interested || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/profile/pro-interest");
      setUser(data);
      setInterested(true);
      toast.success("Kiitos! Ilmoitamme sinulle, kun Pro-jäsenyys julkaistaan.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Jokin meni pieleen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-8 border border-[#CFE0F5] bg-gradient-to-br from-[#EAF2FC] to-white soft-shadow" data-testid="pro-coming-soon">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#292827] text-white px-3 py-1 text-[11px] font-medium"><Crown size={12} /> Pro</span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#5B7FB0]">Tulossa pian</span>
      </div>
      <h3 className="mt-4 text-xl font-semibold tracking-tightish text-[#292827]">Pro-jäsenyys tulossa pian!</h3>
      <p className="mt-1.5 text-[15px] text-[#6E675C]">Pro-jäsenenä sinulla on käytössä seuraavat ominaisuudet:</p>
      <ul className="mt-5 space-y-2.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[15px] text-[#3A3733]" data-testid="pro-feature">
            <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#D5E5F9] text-[#292827] shrink-0"><Check size={12} strokeWidth={2.75} /></span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        {interested ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-[#D5E5F9] text-[#292827] px-5 py-2.5 text-[15px] font-medium" data-testid="pro-interest-done">
            <Check size={16} strokeWidth={2.5} /> Ilmoitamme sinulle
          </span>
        ) : (
          <button onClick={notifyMe} disabled={loading} data-testid="pro-interest-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#292827] text-white px-5 py-2.5 text-[15px] font-medium transition-colors hover:bg-[#1c1b1a] disabled:opacity-40 disabled:pointer-events-none">
            <Bell size={16} /> {loading ? "Tallennetaan…" : "Ilmoita minulle"}
          </button>
        )}
      </div>
    </Card>
  );
}

export function StatCard({ label, value, icon: Icon, to }) {
  const cls = `group block rounded-3xl border border-[#E6E1D9] bg-white p-6 soft-shadow hover:soft-shadow-hover hover:-translate-y-1 transition-[transform,box-shadow] duration-300 ease-out${to ? " cursor-pointer" : ""}`;
  const inner = (
    <>
      {Icon && (
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#D5E5F9] text-[#292827] transition-colors duration-300 group-hover:bg-[#292827] group-hover:text-white">
          <Icon size={18} strokeWidth={1.75} />
        </div>
      )}
      <div className="mt-5 text-4xl font-semibold tracking-display text-[#292827] leading-none">{value}</div>
      <div className="mt-2 text-[13px] font-medium text-[#6E675C]">{label}</div>
    </>
  );
  return to
    ? <Link to={to} data-testid="stat-card" className={cls}>{inner}</Link>
    : <div data-testid="stat-card" className={cls}>{inner}</div>;
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <h3 className="text-[19px] font-semibold tracking-tightish text-[#292827]">{children}</h3>
      {action}
    </div>
  );
}

export function EmptyState({ title, subtitle, action }) {
  return (
    <Card className="p-14 text-center" data-testid="empty-state">
      <div className="mx-auto h-11 w-11 rounded-full bg-[#D5E5F9] flex items-center justify-center text-[#292827]"><Inbox size={20} /></div>
      <p className="mt-5 font-medium text-[#292827]">{title}</p>
      {subtitle && <p className="mt-1.5 text-[#6E675C] text-sm max-w-sm mx-auto leading-relaxed">{subtitle}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </Card>
  );
}

export function CampaignCard(props) {
  return isAffiliate(props.c) ? <AffiliateCard {...props} /> : <RegularCampaignCard {...props} />;
}

function RegularCampaignCard({ c, onClick, footer }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const descRef = useRef(null);
  const [clamped, setClamped] = useState(false);
  useEffect(() => {
    const el = descRef.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [c.description]);

  const cats = c.categories?.length ? c.categories : (c.category ? [c.category] : []);
  const cover = c.images?.[0];
  const hasLogo = !!c.business_logo;
  const logoLetter = (c.business_name || "?").slice(0, 1).toUpperCase();
  const budget = c.budget || (c.budget_from && c.budget_to ? `${c.budget_from}–${c.budget_to} €` : "");
  const location = c.location || c.work_location || "";
  const hasMeta = budget || location || c.creators_needed || c.application_deadline || c.deadline;
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  return (
    <Card className="group flex flex-col overflow-hidden rounded-3xl soft-shadow transition-[transform,box-shadow] duration-300 ease-out cursor-pointer hover:soft-shadow-hover hover:-translate-y-1"
      onClick={onClick} data-testid="campaign-card">
      {cover && (
        <div className="h-48 w-full overflow-hidden bg-[#E9EAEC]">
          <img src={fileUrl(cover)} alt="" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
        </div>
      )}
      {/* Header: small avatar + company + status (landing example style) */}
      <div className="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 flex items-center justify-center shrink-0" data-testid="campaign-card-logo">
              {hasLogo
                ? <img src={fileUrl(c.business_logo)} alt="" loading="eager" decoding="async" className="h-full w-full object-contain" />
                : <span className="text-lg font-semibold text-[#292827]">{logoLetter}</span>}
            </div>
            <span className="min-w-0 truncate text-[13px] text-[#6E675C]" data-testid="campaign-card-company">{c.business_name}</span>
          </div>
          <span className="shrink-0"><StatusBadge status={c.is_expired ? "expired" : c.status} /></span>
        </div>
        {/* Emphasized campaign title */}
        <h4 className="mt-4 text-lg md:text-xl font-semibold tracking-display text-[#292827] leading-snug break-words" data-testid="campaign-card-title">{c.title}</h4>
        {cats.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {cats.slice(0, 3).map((t) => <Tag key={t}>{t}</Tag>)}
          </div>
        )}
        {hasMeta && (
          <div className="mt-4 pt-4 border-t border-[#E7F0FB] flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-[#6E675C]">
            {budget && <span className="flex items-center gap-1.5"><Wallet size={14} className="text-[#292827] shrink-0" /> {budget}</span>}
            {location && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#292827] shrink-0" /> {location}</span>}
            {c.creators_needed && <span className="flex items-center gap-1.5"><Users size={14} className="text-[#292827] shrink-0" /> {c.creators_needed} sisällöntuottajaa</span>}
            {(c.application_deadline || c.deadline) && <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#292827] shrink-0" /> {c.application_deadline || c.deadline}</span>}
          </div>
        )}
      </div>
      {/* Content: description + read-more + apply */}
      <div className="px-6 pb-6 flex flex-col flex-1">
        <p ref={descRef} className="text-[15px] text-[#3A3733] leading-relaxed line-clamp-3" data-testid="campaign-card-description">{c.description}</p>
        {clamped && (
          <button onClick={(e) => { stop(e); navigate(`/dashboard/kampanjat/${c.campaign_id}`); }} data-testid="campaign-card-readmore"
            className="mt-2 self-start text-[13px] font-medium text-[#5B7FB0] hover:text-[#292827] transition-colors">
            Lue lisää
          </button>
        )}
        {footer}
        {user?.role === "creator" && (
          c.has_applied ? (
            <div className="mt-auto pt-5 w-full" data-testid="campaign-applied-badge">
              <span className="flex items-center justify-center gap-2 w-full rounded-full bg-[#EFEBE4] text-[#6E675C] px-5 py-3 text-[15px] font-medium">
                <Check size={16} strokeWidth={2.5} className="text-[#292827]" /> Hakemus jätetty
              </span>
            </div>
          ) : (
            <button onClick={(e) => { stop(e); navigate(`/dashboard/kampanjat/${c.campaign_id}`); }} data-testid="campaign-apply-btn"
              className="mt-auto pt-5 w-full">
              <span className="block w-full rounded-full bg-[#292827] text-white px-5 py-3 text-[15px] font-medium transition-colors group-hover:bg-[#1c1b1a] hover:!bg-[#1c1b1a]">
                Hae kampanjaan
              </span>
            </button>
          )
        )}
      </div>
    </Card>
  );
}

export function DetailRow({ icon: Icon, label, value, dark = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${dark ? "bg-[#292827] text-white" : "bg-[#D5E5F9] text-[#292827]"}`}><Icon size={15} /></div>
      <div>
        <div className="text-xs text-[#968F85]">{label}</div>
        <div className="text-sm font-medium text-[#292827]">{value}</div>
      </div>
    </div>
  );
}

export { Globe };
